import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FEATURES } from '@yuan-math/config';
import type { GradeResult } from '@yuan-math/exercises';
import { allowanceMessage, comboMultiplier } from '@yuan-math/learning';
import { Button, Card, Confetti, Mascot, MasteryBar } from '@yuan-math/ui';
import { ExerciseRunner } from '../components/ExerciseRunner.tsx';
import { bank, registry, useLearner } from '../state/LearnerContext.tsx';
import { rewardsFor, useRewards } from '../state/RewardsContext.tsx';
import { NotFoundPage } from './NotFoundPage.tsx';

const SET_SIZE = 8;

/** How far through one set the student is. Keyed, so a new set starts over. */
type Progress = {
  key: string;
  index: number;
  answered: { correct: boolean }[];
  /** XP earned in this set, so the summary can report the sitting, not the total. */
  xp: number;
};

const freshProgress = (key: string): Progress => ({ key, index: 0, answered: [], xp: 0 });

const newSeed = (): string => `session-${Date.now().toString(36)}`;

/**
 * A practice session.
 *
 * The set is generated once per session from a stable seed, so refreshing the
 * page does not silently swap the questions underneath the student. Attempts
 * are recorded as they happen — closing the tab halfway loses no work.
 */
export function PracticePage(): JSX.Element {
  const { conceptId } = useParams();
  const [params, setParams] = useSearchParams();
  const { recordAttempt, signedIn, masteryOf, allowance, today, quests } = useLearner();
  const { celebrate } = useRewards();

  /*
   * The URL is the only source of truth for the seed.
   *
   * Capturing it in state at mount instead is what made "Another set" look
   * dead: the link changes the query string, but a navigation within the same
   * route does not remount the page, so a seed read once never moved and the
   * student stared at the same completion screen. Reading it from the params
   * every render is also what makes the promise above true — without the seed
   * written back to the URL, a refresh mid-set deals eight new questions.
   */
  const [mountSeed] = useState(newSeed);
  const seed = params.get('seed') ?? mountSeed;

  useEffect(() => {
    if (params.get('seed')) return;
    const next = new URLSearchParams(params);
    next.set('seed', mountSeed);
    setParams(next, { replace: true });
  }, [params, mountSeed, setParams]);

  const setKey = `${conceptId ?? ''}:${seed}`;
  const [stored, setStored] = useState<Progress>(() => freshProgress(setKey));

  /*
   * A different set — a new seed, or a different concept — starts at question
   * one. Derived while rendering rather than reset in an effect, so the first
   * frame after the URL changes already shows the new set; an effect would
   * flash the old screen first.
   */
  const progress = stored.key === setKey ? stored : freshProgress(setKey);
  const { index, answered } = progress;

  const concept = conceptId ? registry.concept(conceptId) : undefined;

  const exercises = useMemo(
    () => (conceptId ? bank.practiceSet({ conceptId, count: SET_SIZE, seed }) : []),
    [conceptId, seed],
  );

  const advance = useCallback(() => {
    setStored({ ...progress, index: progress.index + 1 });
  }, [progress]);

  const handleGraded = useCallback(
    (result: GradeResult, meta: { responseMs: number; firstTry: boolean; usedHint: boolean }) => {
      const exercise = exercises[index];
      if (!exercise || !conceptId) return;
      if (result.status === 'malformed') return;

      let earned = 0;
      if (signedIn) {
        const feedback = recordAttempt({
          conceptId,
          exerciseId: exercise.id,
          difficulty: exercise.difficulty,
          result,
          responseMs: meta.responseMs,
          usedHint: meta.usedHint,
          firstTry: meta.firstTry,
        });
        earned = feedback.xpGained + feedback.bonusXp;
        celebrate(
          rewardsFor(
            {
              unlockedAchievements: feedback.unlockedAchievements,
              completedQuests: feedback.completedQuests,
              sweptTheDay: feedback.sweptTheDay,
              justMastered: feedback.justMastered,
              masteredConceptTitle: concept?.title,
              ...(feedback.leveledUpTo !== undefined
                ? { leveledUpTo: feedback.leveledUpTo, levelTitle: feedback.levelTitle }
                : {}),
            },
            Date.now(),
          ),
        );
      }
      // Only the first attempt on an item counts towards the session score.
      if (meta.firstTry) {
        setStored({
          ...progress,
          answered: [...progress.answered, { correct: result.correct }],
          xp: progress.xp + earned,
        });
      } else if (earned > 0) {
        setStored({ ...progress, xp: progress.xp + earned });
      }
    },
    [celebrate, concept?.title, conceptId, exercises, index, progress, recordAttempt, signedIn],
  );

  if (!concept || !conceptId) return <NotFoundPage />;

  /*
   * The free daily cap.
   *
   * Deliberately placed here and nowhere else: it limits how many *problems*
   * can be generated, and never a lesson, the Math Map or a student's own
   * progress. A child who cannot pay is not taught less mathematics — they are
   * asked to come back tomorrow for more drill.
   */
  if (allowance.exhausted) {
    return (
      <div className="page page--narrow">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to={`/concepts/${conceptId}`}>{concept.title}</Link>
        </nav>
        <h1 className="page__title">That is today's practice done.</h1>
        <p className="page__lead">{allowanceMessage(allowance)}</p>

        <Card>
          <p>
            You answered {allowance.usedToday} questions today. Reading a lesson again is often worth more than
            another twenty problems anyway — the practice you have done needs time to settle.
          </p>
        </Card>

        <div className="button-row">
          <Link to={`/concepts/${conceptId}`} className="btn btn--primary">
            Back to {concept.title}
          </Link>
          <Link to="/map" className="btn btn--secondary">
            Explore the Math Map
          </Link>
          {FEATURES.payments ? (
            <Link to="/pricing" className="btn btn--ghost">
              See the plans
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  if (exercises.length === 0) {
    return (
      <div className="page page--narrow">
        <h1 className="page__title">{concept.title}</h1>
        <p className="page__lead">There are no exercises for this concept yet. It is on the map, not in the bank.</p>
        <Link to="/map" className="btn btn--secondary">
          Back to the Math Map
        </Link>
      </div>
    );
  }

  const exercise = exercises[index];
  const finished = index >= exercises.length;
  const correctCount = answered.filter((entry) => entry.correct).length;
  const mastery = masteryOf(conceptId);
  const questsDone = quests.filter((entry) => entry.complete).length;

  if (finished || !exercise) {
    const accuracy = answered.length === 0 ? 0 : correctCount / answered.length;
    /*
     * The mascot reports the sitting honestly.
     *
     * It celebrates a strong set and stays *thoughtful* rather than sad about a
     * weak one — a set that went badly is a set that taught something, and a
     * miserable face at the end of it is the fastest way to make a child stop
     * coming back. Confetti is reserved for the strong set, so it keeps meaning
     * something.
     */
    const strong = accuracy >= 0.8 && answered.length > 0;
    return (
      <div className="page page--narrow">
        <div className="session-done">
          <Mascot mood={strong ? 'celebrate' : 'thinking'} size={96} />
          <h1 className="page__title">Session complete</h1>
          <p className="page__lead">
            {correctCount} of {answered.length} right on the first try
            {accuracy >= 0.8 ? ' — that is solid work.' : '. The explanations are worth a second read.'}
          </p>
          {strong ? <Confetti /> : null}
        </div>

        {signedIn ? <MasteryBar value={mastery.overall} label={`${concept.title} mastery`} size="lg" /> : null}

        {signedIn ? (
          <div className="session-summary">
            <span className="session-summary__item">
              <strong>+{progress.xp}</strong> XP this set
            </span>
            <span className="session-summary__item">
              <strong>{today.bestCombo}</strong> best run today
            </span>
            <span className="session-summary__item">
              <strong>{questsDone}</strong> of {quests.length} quests done
            </span>
          </div>
        ) : null}

        <div className="button-row">
          <Link to={`/practice/${conceptId}?seed=${newSeed()}`} replace className="btn btn--primary">
            Another set
          </Link>
          <Link to={`/concepts/${conceptId}`} className="btn btn--secondary">
            Back to the concept
          </Link>
          {signedIn ? (
            <Link to="/home" className="btn btn--ghost">
              What should I do next?
            </Link>
          ) : null}
        </div>

        {!signedIn ? (
          <p className="page__note">
            You are practising as a guest, so none of this was saved. <Link to="/start">Create a learner</Link> to keep
            your mastery.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="page page--narrow">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to={`/concepts/${conceptId}`}>{concept.title}</Link>
        <span aria-hidden="true">/</span>
        <span>Practice</span>
      </nav>

      <header className="practice-header">
        <div>
          <h1 className="page__title">{concept.title}</h1>
          <p className="practice-header__meta">
            Question {index + 1} of {exercises.length} · difficulty {exercise.difficulty} of 5
            {allowance.remaining !== undefined && allowance.remaining <= 10
              ? ` · ${allowance.remaining} left today`
              : ''}
          </p>
        </div>
        {/* The run is shown only once it is real. A "0 in a row" counter on
            the first question invents a thing to lose before anything has
            been won. */}
        {signedIn && today.combo >= 2 ? (
          <p className="combo" aria-label={`${today.combo} correct in a row`}>
            <span className="combo__flame" aria-hidden="true">
              🔥
            </span>
            <span className="combo__count">{today.combo} in a row</span>
            <span className="combo__multiplier">×{comboMultiplier(today.combo).toFixed(2).replace(/0$/, '')} XP</span>
          </p>
        ) : null}

        <div className="practice-progress" role="progressbar" aria-valuenow={index} aria-valuemin={0} aria-valuemax={exercises.length}>
          {exercises.map((entry, i) => (
            <span
              key={entry.id}
              className={`practice-progress__dot ${i < answered.length ? (answered[i]?.correct ? 'is-correct' : 'is-incorrect') : ''} ${i === index ? 'is-current' : ''}`}
            />
          ))}
        </div>
      </header>

      <ExerciseRunner exercise={exercise} onGraded={handleGraded} showNext onNext={advance} />

      <div className="practice-footer">
        <Button variant="ghost" size="sm" onClick={advance}>
          Skip this one
        </Button>
      </div>
    </div>
  );
}
